import * as symbols from '../../../../../enum/symbols';
import React, { useMemo } from 'react';
import { Text, View, Image } from '@react-pdf/renderer';
import { pdfSharedStyles } from '../../../pdfSharedStyles';
import { TFunction } from 'i18next';
import { IGeogridData, IGeogridItem } from '../../../../../api/IApiResponse';
import { ProductDataService } from '../../../../../services/ProductDataService';
import { getTensileAtStrain } from '../../../../../services/TensileAtStrength';
import { calculateGeogridRequiredStrengthUnified, calculateGeogridStrengthBreakdownUnified } from '../../../../../services/GeogridTtotService';


// Funzione di parsing base per <br> e <strong>
import { ValueUnitCells } from '../../shared/pdfUnitMerge';

function parseHtmlToPdfText(html: string, style?: any) {
    // Sostituisce <br> con \n
    const withBreaks = html.replace(/<br\s*\/??\s*>/gi, '\n');
    // Regex per trovare <strong>...</strong>
    const regex = /(<strong>(.*?)<\/strong>)/gi;
    const result: JSX.Element[] = [];
    let lastIndex = 0;
    let match;
    let key = 0;

    while ((match = regex.exec(withBreaks)) !== null) {
        // Testo prima del tag
        if (match.index > lastIndex) {
            const text = withBreaks.substring(lastIndex, match.index);
            if (text) result.push(<Text key={key++} style={style}>{text}</Text>);
        }
        // Testo in grassetto
        const boldText = match[2];
        if (boldText) {
            result.push(
                <Text
                    key={key++}
                    style={{
                        ...style,
                        fontFamily: 'DejaVuSans',
                        fontWeight: 'bold',
                    }}
                >
                    {boldText}
                </Text>
            );
        }
        lastIndex = regex.lastIndex;
    }
    // Testo dopo l'ultimo tag
    if (lastIndex < withBreaks.length) {
        const text = withBreaks.substring(lastIndex);
        if (text) result.push(<Text key={key++} style={style}>{text}</Text>);
    }
    return result;
}

interface RailwayGeoGridCoreProps {
    geoGridData?: IGeogridData;
    geogridImage?: string;
    projectData: any;
    t: TFunction;
    showInputData?: boolean;
    showGeoGridImage?: boolean;
    showCourseResults?: boolean;
    customTitle?: string;
}

export const RailwayGeoGridCore: React.FC<RailwayGeoGridCoreProps> = ({
    geoGridData,
    geogridImage,
    projectData,
    t,
    showInputData = true,
    showGeoGridImage = true,
    showCourseResults = true,
    customTitle,
}) => {
    const unitSystem = projectData?.unit || 'Metric';
    const projectType = projectData?.type || 'geogrid';
    const projectGeoGridData = projectData?.projectInfo?.data?.geoGrid;

    // Direct conversion factors for Imperial unit conversion
    // Reason: convertFieldValue from UnitConversionService doesn't support all unit pairs (kN→lbf, kN/m→lbf/ft)
    const CONVERSION_FACTORS: Record<string, number> = {
        'm_ft': 3.28084,
        'kN_lbf': 224.8089,
        'kPa_psi': 0.145038,
        'kPa_psf': 20.8854,
        'kN/m³_pcf': 6.36588,
        'kN/m_lbf/ft': 68.521959,
        'MPa_psi': 145.038,
    };

    const getConversionFactor = (metricUnit: string, imperialUnit: string): number => {
        const key = `${metricUnit}_${imperialUnit}`;
        return CONVERSION_FACTORS[key] || 1;
    };

    // Convert numeric value from Metric to Imperial when needed
    const convertNumericValue = (value: number | null | undefined, metricUnit: string, imperialUnit: string): string => {
        if (value === null || value === undefined) return 'N/A';
        if (unitSystem === 'Imperial') {
            const factor = getConversionFactor(metricUnit, imperialUnit);
            return (value * factor).toFixed(2);
        }
        return value.toFixed(2);
    };

    // Extract GeoGrid course data
    const baseThickness = geoGridData?.geogridBaseCourse?.thickness ?? 0;
    const subbaseThickness = geoGridData?.geogridSubBaseCourse?.thickness ?? 0;
    const baseGeogrids = geoGridData?.geogridBaseCourse?.geogrids ?? [];
    const subbaseGeogrids = geoGridData?.geogridSubBaseCourse?.geogrids ?? [];
    const tipoGeoGrid = geoGridData?.type;
    // Reason: Railway projects always use Rectangular geometry (deck-based loading)
    // Railway type should be treated as Rectangular for Load section display
    const isRectangular = tipoGeoGrid?.geogridType === 'Rectangular' || tipoGeoGrid?.geogridType === 'Railway' || projectType === 'railways';

    // Helper function to get unit based on system
    const getUnit = (metricUnit: string, imperialUnit: string) => {
        return unitSystem === 'Imperial' ? imperialUnit : metricUnit;
    };

    // Helper to format numbers to 2 decimal places
    const formatValue = (value: unknown): string => {
        if (typeof value === 'number') {
            return value.toFixed(2);
        }
        return String(value);
    };

    // Format course data for PDF table
    type TableRow = [string, string | React.ReactElement, string, string];
    type ProductRow = { type: 'productRow'; description: string; value: string };
    type FullRow = { type: 'fullRow'; text: string };
    type TableDataRow = TableRow | ProductRow | FullRow;

    const formatCourseDataForPdfTable = (
        courseTitleKey: string,
        thickness: number,
        geogrids: IGeogridItem[],
        t: TFunction,
        projectGeoGridData?: IGeogridData,
        getUnitFn?: (metricUnit: string, imperialUnit: string) => string
    ): TableDataRow[] => {
        const tableData: TableDataRow[] = [];
        // Use provided getUnit function or default to metric
        const unitFn = getUnitFn || ((metric: string) => metric);

        // Reason: Railway projects use ballast/subballast terminology instead of base/subbase
        const isBaseCourse = courseTitleKey.includes('geogrid_base_');
        const thicknessKey = isBaseCourse ? 'graphic.table.ballast_thickness' : 'graphic.table.subballast_thickness';
        const thicknessLabel = t(thicknessKey) || (isBaseCourse ? 'Ballast Thickness' : 'Subballast Thickness');

        tableData.push([thicknessLabel, 't', convertNumericValue(thickness, 'm', 'ft'), unitFn('m', 'ft')]);

        geogrids.forEach((geogrid, index) => {
            const geogridNum = index + 1;
            const productFamily = geogrid.type;
            const productName = geogrid.selectedGeogrid;
            const designStrain = geogrid.designStrain;

            // Fixed: Check for 'subbase' in the key to properly distinguish between base and subbase
            // Railway uses 'geogrid_base_course_railway' and 'geogrid_subbase_course_railway'
            const courseType: 'base' | 'subbase' = courseTitleKey.includes('subbase') ? 'subbase' : 'base';
            const calculatedRequiredStrength = calculateGeogridRequiredStrengthUnified(projectGeoGridData, index, courseType);
            const calculatedNominalStrength = getTensileAtStrain(ProductDataService.getAllProducts(), productFamily, productName, designStrain);
            const fallbackRequiredStrength = getTensileAtStrain(ProductDataService.getAllProducts(), productFamily, productName, designStrain);

            // Get tensile breakdown (Tz, Tp, Tm)
            const breakdown = calculateGeogridStrengthBreakdownUnified(projectGeoGridData, index, courseType);

            const numericReqStrength = calculatedRequiredStrength ?? fallbackRequiredStrength;
            const numericNomStrength = calculatedNominalStrength;

            const displayRequiredStrength = numericReqStrength !== null && numericReqStrength !== undefined ? convertNumericValue(numericReqStrength, 'kN/m', 'lbf/ft') : (t('common.not_available', 'N/A') ?? 'N/A');
            const displayNominalStrength = numericNomStrength !== null && numericNomStrength !== undefined ? convertNumericValue(numericNomStrength, 'kN/m', 'lbf/ft') : (t('common.not_available', 'N/A') ?? 'N/A');

            tableData.push({ type: 'productRow', description: t('graphic.table.geogrid_product', { num: geogridNum }), value: geogrid.selectedGeogrid || t('common.not_available', 'N/A') });
            tableData.push([
                t('graphic.table.geogrid_position', { num: geogridNum }),
                symbols.sym_pgeo(geogridNum),
                geogrid.position !== null && geogrid.position !== undefined ? convertNumericValue(geogrid.position, 'm', 'ft') : (t('common.not_available', 'N/A') ?? 'N/A'),
                unitFn('m', 'ft'),
            ]);
            tableData.push([
                t('graphic.table.geogrid_strain', { num: geogridNum }),
                symbols.sym_ed(geogridNum),
                (geogrid.designStrain?.toString() || t('common.not_available', 'N/A')) ?? 'N/A',
                '%',
            ]);
            // Tensile contribution breakdown rows
            tableData.push([
                t('graphic.table.geogrid_earth_pressure'),
                symbols.sym_tz(geogridNum),
                breakdown?.Tz !== null && breakdown?.Tz !== undefined ? convertNumericValue(breakdown.Tz, 'kN/m', 'lbf/ft') : (t('common.not_available', 'N/A') ?? 'N/A'),
                unitFn('kN/m', 'lbf/ft'),
            ]);
            tableData.push([
                t('graphic.table.geogrid_tension_component'),
                symbols.sym_tp(geogridNum),
                breakdown?.Tp !== null && breakdown?.Tp !== undefined ? convertNumericValue(breakdown.Tp, 'kN/m', 'lbf/ft') : (t('common.not_available', 'N/A') ?? 'N/A'),
                unitFn('kN/m', 'lbf/ft'),
            ]);
            tableData.push([
                t('graphic.table.geogrid_membrane_effect'),
                symbols.sym_tm(geogridNum),
                breakdown?.Tm !== null && breakdown?.Tm !== undefined ? convertNumericValue(breakdown.Tm, 'kN/m', 'lbf/ft') : (t('common.not_available', 'N/A') ?? 'N/A'),
                unitFn('kN/m', 'lbf/ft'),
            ]);
            tableData.push([
                t('graphic.table.geogrid_req_strength', { num: geogridNum }),
                symbols.sym_treq(geogridNum),
                displayRequiredStrength,
                unitFn('kN/m', 'lbf/ft'),
            ]);
            tableData.push([
                t('graphic.table.geogrid_nom_strength', { num: geogridNum }),
                symbols.sym_tnom(geogridNum),
                displayNominalStrength,
                unitFn('kN/m', 'lbf/ft'),
            ]);

            let statusText: string;
            if (numericNomStrength !== null && numericReqStrength !== null && numericNomStrength >= numericReqStrength) {
                statusText = t('graphic.table.verified', 'Verified');
            } else if (numericNomStrength === null || numericReqStrength === null) {
                statusText = t('graphic.table.data_missing_for_verification', 'Data Missing for Verification');
            } else {
                statusText = t('graphic.table.not_verified', 'Not Verified');
            }
            tableData.push({ type: 'fullRow', text: statusText });
        });

        return tableData;
    };

    const formattedBaseCourseData = useMemo(
        () => formatCourseDataForPdfTable('geogrid_base_course_railway', baseThickness, baseGeogrids, t, projectGeoGridData, getUnit),
        [baseThickness, baseGeogrids, t, projectGeoGridData, getUnit]
    );

    const formattedSubbaseCourseData = useMemo(
        () => formatCourseDataForPdfTable('geogrid_subbase_course_railway', subbaseThickness, subbaseGeogrids, t, projectGeoGridData, getUnit),
        [subbaseThickness, subbaseGeogrids, t, projectGeoGridData, getUnit]
    );

    return (
        <>
            {/* Title Section */}
            {/* Project Type Header */}
            <View style={pdfSharedStyles.spacer1} />
            <Text style={[pdfSharedStyles.projectInfoProjectType]}>GROUNDMAC - Railway</Text>
            <View style={pdfSharedStyles.spacer0} />

            <Text>{parseHtmlToPdfText(t('railway_GeoGrid_project_info_desc_pdf'), pdfSharedStyles.projectInfoSubtitle)}</Text>

            {/* Input Data Summary Section */}
            {showInputData && (
                <View style={pdfSharedStyles.section}>
                    <View style={pdfSharedStyles.spacer1} />
                    <Text style={[pdfSharedStyles.title, { textAlign: 'left' }]}>{t('input_data_summary')}</Text>
                    <View style={pdfSharedStyles.spacer0} />
                    <View style={pdfSharedStyles.innSection}>
                        {/* Load Table */}
                        {geoGridData?.load && (
                            <View style={pdfSharedStyles.colHalf}>
                                <Text style={pdfSharedStyles.tableTitle}>{t('load')}</Text>
                                <View style={pdfSharedStyles.spacer0} />
                                <View style={pdfSharedStyles.table}>
                                    <View style={pdfSharedStyles.tableRow}>
                                        <View style={[pdfSharedStyles.tableColHeader, pdfSharedStyles.dimFirstCol]}>
                                            <Text style={[pdfSharedStyles.tableCellHeader, pdfSharedStyles.leftCell]}>{t('output_description')}</Text>
                                        </View>
                                        <View style={pdfSharedStyles.tableColHeaderSymbol}>
                                            <Text style={pdfSharedStyles.tableCellHeader}>{t('output_symbol')}</Text>
                                        </View>
                                        <View style={pdfSharedStyles.tableColHeaderValue}>
                                            <Text style={pdfSharedStyles.tableCellHeader}>{t('output_value')}</Text>
                                        </View>
                                        <View style={pdfSharedStyles.tableColHeaderUnit}>
                                            <Text style={pdfSharedStyles.tableCellHeader}>{t('output_unit')}</Text>
                                        </View>
                                    </View>
                                    {isRectangular ? (
                                        <>
                                            {geoGridData.load.deckHalfWidth !== undefined && (
                                                <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.evenRow]}>
                                                    <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                        <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('load.deckHalfWidth')}</Text>
                                                    </View>
                                                    <View style={pdfSharedStyles.tableColSymbol}>
                                                        <Text style={pdfSharedStyles.tableCell}>B</Text>
                                                    </View>
                                                    <View style={pdfSharedStyles.tableColValue}>
                                                        <Text style={pdfSharedStyles.tableCell}>{formatValue(geoGridData.load.deckHalfWidth)}</Text>
                                                    </View>
                                                    <View style={pdfSharedStyles.tableColUnit}>
                                                        <Text style={pdfSharedStyles.tableCell}>{getUnit('m', 'ft')}</Text>
                                                    </View>
                                                </View>
                                            )}
                                            {geoGridData.load.deckHalfLength !== undefined && (
                                                <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.oddRow]}>
                                                    <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                        <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('load.deckHalfLength')}</Text>
                                                    </View>
                                                    <View style={pdfSharedStyles.tableColSymbol}>
                                                        <Text style={pdfSharedStyles.tableCell}>L</Text>
                                                    </View>
                                                    <View style={pdfSharedStyles.tableColValue}>
                                                        <Text style={pdfSharedStyles.tableCell}>{formatValue(geoGridData.load.deckHalfLength)}</Text>
                                                    </View>
                                                    <View style={pdfSharedStyles.tableColUnit}>
                                                        <Text style={pdfSharedStyles.tableCell}>{getUnit('m', 'ft')}</Text>
                                                    </View>
                                                </View>
                                            )}
                                            {geoGridData.load.uniformLoad !== undefined && (
                                                <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.evenRow]}>
                                                    <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                        <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('load.uniformLoad')}</Text>
                                                    </View>
                                                    <View style={pdfSharedStyles.tableColSymbol}>
                                                        <Text style={pdfSharedStyles.tableCell}>q</Text>
                                                    </View>
                                                    <View style={pdfSharedStyles.tableColValue}>
                                                        <Text style={pdfSharedStyles.tableCell}>{formatValue(geoGridData.load.uniformLoad)}</Text>
                                                    </View>
                                                    <View style={pdfSharedStyles.tableColUnit}>
                                                        <Text style={pdfSharedStyles.tableCell}>{getUnit('kPa', 'psf')}</Text>
                                                    </View>
                                                </View>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            {geoGridData.load.axleLoad !== undefined && (
                                                <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.evenRow]}>
                                                    <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                        <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('load.axleLoad')}</Text>
                                                    </View>
                                                    <View style={pdfSharedStyles.tableColSymbol}>
                                                        <Text style={pdfSharedStyles.tableCell}>P</Text>
                                                    </View>
                                                    <View style={pdfSharedStyles.tableColValue}>
                                                        <Text style={pdfSharedStyles.tableCell}>{formatValue(geoGridData.load.axleLoad)}</Text>
                                                    </View>
                                                    <View style={pdfSharedStyles.tableColUnit}>
                                                        <Text style={pdfSharedStyles.tableCell}>{getUnit('kN', 'lbf')}</Text>
                                                    </View>
                                                </View>
                                            )}
                                            {geoGridData.load.tireInflationPressure !== undefined && (
                                                <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.oddRow]}>
                                                    <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                        <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('load.tireInflationPressure')}</Text>
                                                    </View>
                                                    <View style={pdfSharedStyles.tableColSymbol}>
                                                        <Text style={pdfSharedStyles.tableCell}>p</Text>
                                                    </View>
                                                    <View style={pdfSharedStyles.tableColValue}>
                                                        <Text style={pdfSharedStyles.tableCell}>{formatValue(geoGridData.load.tireInflationPressure)}</Text>
                                                    </View>
                                                    <View style={pdfSharedStyles.tableColUnit}>
                                                        <Text style={pdfSharedStyles.tableCell}>{getUnit('kPa', 'psi')}</Text>
                                                    </View>
                                                </View>
                                            )}
                                        </>
                                    )}
                                </View>
                            </View>
                        )}

                        {/* Ballast Table (Base Course) */}
                        {geoGridData?.baseCourse && baseThickness > 0 && (
                            <View style={pdfSharedStyles.colHalf}>
                                <Text style={pdfSharedStyles.tableTitle}>{t('ballast') || 'Ballast'}</Text>
                                <View style={pdfSharedStyles.spacer0} />
                                <View style={pdfSharedStyles.table}>
                                    <View style={pdfSharedStyles.tableRow}>
                                        <View style={[pdfSharedStyles.tableColHeader, pdfSharedStyles.dimFirstCol]}>
                                            <Text style={[pdfSharedStyles.tableCellHeader, pdfSharedStyles.leftCell]}>{t('output_description')}</Text>
                                        </View>
                                        <View style={pdfSharedStyles.tableColHeaderSymbol}>
                                            <Text style={pdfSharedStyles.tableCellHeader}>{t('output_symbol')}</Text>
                                        </View>
                                        <View style={pdfSharedStyles.tableColHeaderValue}>
                                            <Text style={pdfSharedStyles.tableCellHeader}>{t('output_value')}</Text>
                                        </View>
                                        <View style={pdfSharedStyles.tableColHeaderUnit}>
                                            <Text style={pdfSharedStyles.tableCellHeader}>{t('output_unit')}</Text>
                                        </View>
                                    </View>
                                    {geoGridData.baseCourse.unitWeight !== undefined && (
                                        <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.evenRow]}>
                                            <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('baseCourse.unitWeight')}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColSymbol}>
                                                <Text style={pdfSharedStyles.tableCell}>γ</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColValue}>
                                                <Text style={pdfSharedStyles.tableCell}>{formatValue(geoGridData.baseCourse.unitWeight)}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColUnit}>
                                                <Text style={pdfSharedStyles.tableCell}>{getUnit('kN/m³', 'pcf')}</Text>
                                            </View>
                                        </View>
                                    )}
                                    {geoGridData.baseCourse.frictionAngle !== undefined && (
                                        <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.oddRow]}>
                                            <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('baseCourse.frictionAngle')}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColSymbol}>
                                                <Text style={pdfSharedStyles.tableCell}>φ</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColValue}>
                                                <Text style={pdfSharedStyles.tableCell}>{formatValue(geoGridData.baseCourse.frictionAngle)}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColUnit}>
                                                <Text style={pdfSharedStyles.tableCell}>°</Text>
                                            </View>
                                        </View>
                                    )}
                                    {geoGridData.baseCourse.loadSpreadingAngle !== undefined && (
                                        <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.evenRow]}>
                                            <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('baseCourse.loadSpreadingAngle')}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColSymbol}>
                                                <Text style={pdfSharedStyles.tableCell}>α</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColValue}>
                                                <Text style={pdfSharedStyles.tableCell}>{formatValue(geoGridData.baseCourse.loadSpreadingAngle)}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColUnit}>
                                                <Text style={pdfSharedStyles.tableCell}>°</Text>
                                            </View>
                                        </View>
                                    )}
                                    {geoGridData.baseCourse.cohesion !== undefined && (
                                        <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.oddRow]}>
                                            <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('baseCourse.cohesion')}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColSymbol}>
                                                <Text style={pdfSharedStyles.tableCell}>c</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColValue}>
                                                <Text style={pdfSharedStyles.tableCell}>{formatValue(geoGridData.baseCourse.cohesion)}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColUnit}>
                                                <Text style={pdfSharedStyles.tableCell}>{getUnit('kPa', 'psf')}</Text>
                                            </View>
                                        </View>
                                    )}
                                </View>
                            </View>
                        )}

                        {/* Truck Load For First Lift Table */}
                        {geoGridData?.truck && (
                            <View style={pdfSharedStyles.colHalf}>
                                <Text style={pdfSharedStyles.tableTitle}>{t('truck') || 'Truck Load For First Lift'}</Text>
                                <View style={pdfSharedStyles.spacer0} />
                                <View style={pdfSharedStyles.table}>
                                    <View style={pdfSharedStyles.tableRow}>
                                        <View style={[pdfSharedStyles.tableColHeader, pdfSharedStyles.dimFirstCol]}>
                                            <Text style={[pdfSharedStyles.tableCellHeader, pdfSharedStyles.leftCell]}>{t('output_description')}</Text>
                                        </View>
                                        <View style={pdfSharedStyles.tableColHeaderSymbol}>
                                            <Text style={pdfSharedStyles.tableCellHeader}>{t('output_symbol')}</Text>
                                        </View>
                                        <View style={pdfSharedStyles.tableColHeaderValue}>
                                            <Text style={pdfSharedStyles.tableCellHeader}>{t('output_value')}</Text>
                                        </View>
                                        <View style={pdfSharedStyles.tableColHeaderUnit}>
                                            <Text style={pdfSharedStyles.tableCellHeader}>{t('output_unit')}</Text>
                                        </View>
                                    </View>
                                    {geoGridData.truck.wheelRadius !== undefined && (
                                        <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.evenRow]}>
                                            <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('truck.wheelRadius') || 'Wheel Radius'}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColSymbol}>
                                                <Text style={pdfSharedStyles.tableCell}>r</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColValue}>
                                                <Text style={pdfSharedStyles.tableCell}>{formatValue(geoGridData.truck.wheelRadius)}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColUnit}>
                                                <Text style={pdfSharedStyles.tableCell}>{getUnit('m', 'ft')}</Text>
                                            </View>
                                        </View>
                                    )}
                                    {geoGridData.truck.wheelWidth !== undefined && (
                                        <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.oddRow]}>
                                            <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('truck.wheelWidth') || 'Wheel Width'}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColSymbol}>
                                                <Text style={pdfSharedStyles.tableCell}>b</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColValue}>
                                                <Text style={pdfSharedStyles.tableCell}>{formatValue(geoGridData.truck.wheelWidth)}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColUnit}>
                                                <Text style={pdfSharedStyles.tableCell}>{getUnit('m', 'ft')}</Text>
                                            </View>
                                        </View>
                                    )}
                                    {geoGridData.truck.wheelLoad !== undefined && (
                                        <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.evenRow]}>
                                            <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('truck.wheelLoad') || 'Wheel Load'}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColSymbol}>
                                                <Text style={pdfSharedStyles.tableCell}>P</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColValue}>
                                                <Text style={pdfSharedStyles.tableCell}>{formatValue(geoGridData.truck.wheelLoad)}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColUnit}>
                                                <Text style={pdfSharedStyles.tableCell}>{getUnit('kN', 'lbf')}</Text>
                                            </View>
                                        </View>
                                    )}
                                    {geoGridData.truck.tirePressure !== undefined && (
                                        <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.oddRow]}>
                                            <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('truck.tirePressure') || 'Tire Pressure'}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColSymbol}>
                                                <Text style={pdfSharedStyles.tableCell}>{symbols.sym_pw}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColValue}>
                                                <Text style={pdfSharedStyles.tableCell}>{formatValue(geoGridData.truck.tirePressure)}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColUnit}>
                                                <Text style={pdfSharedStyles.tableCell}>{getUnit('kPa', 'psi')}</Text>
                                            </View>
                                        </View>
                                    )}
                                    {geoGridData.truck.thickness !== undefined && (
                                        <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.evenRow]}>
                                            <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('truck.thickness') || 'Thickness'}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColSymbol}>
                                                <Text style={pdfSharedStyles.tableCell}>h*</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColValue}>
                                                <Text style={pdfSharedStyles.tableCell}>{formatValue(geoGridData.truck.thickness)}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColUnit}>
                                                <Text style={pdfSharedStyles.tableCell}>{getUnit('m', 'ft')}</Text>
                                            </View>
                                        </View>
                                    )}
                                </View>
                            </View>
                        )}

                        {/* Subballast Table (SubBase Course) */}
                        {geoGridData?.subBaseCourse && subbaseThickness > 0 && (
                            <View style={pdfSharedStyles.colHalf}>
                                <Text style={pdfSharedStyles.tableTitle}>{t('subballast') || 'Subballast'}</Text>
                                <View style={pdfSharedStyles.spacer0} />
                                <View style={pdfSharedStyles.table}>
                                    <View style={pdfSharedStyles.tableRow}>
                                        <View style={[pdfSharedStyles.tableColHeader, pdfSharedStyles.dimFirstCol]}>
                                            <Text style={[pdfSharedStyles.tableCellHeader, pdfSharedStyles.leftCell]}>{t('output_description')}</Text>
                                        </View>
                                        <View style={pdfSharedStyles.tableColHeaderSymbol}>
                                            <Text style={pdfSharedStyles.tableCellHeader}>{t('output_symbol')}</Text>
                                        </View>
                                        <View style={pdfSharedStyles.tableColHeaderValue}>
                                            <Text style={pdfSharedStyles.tableCellHeader}>{t('output_value')}</Text>
                                        </View>
                                        <View style={pdfSharedStyles.tableColHeaderUnit}>
                                            <Text style={pdfSharedStyles.tableCellHeader}>{t('output_unit')}</Text>
                                        </View>
                                    </View>
                                    {geoGridData.subBaseCourse.unitWeight !== undefined && (
                                        <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.evenRow]}>
                                            <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('subBaseCourse.unitWeight') || 'Unit Weight'}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColSymbol}>
                                                <Text style={pdfSharedStyles.tableCell}>γ_sb</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColValue}>
                                                <Text style={pdfSharedStyles.tableCell}>{formatValue(geoGridData.subBaseCourse.unitWeight)}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColUnit}>
                                                <Text style={pdfSharedStyles.tableCell}>{getUnit('kN/m³', 'pcf')}</Text>
                                            </View>
                                        </View>
                                    )}
                                    {geoGridData.subBaseCourse.frictionAngle !== undefined && (
                                        <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.oddRow]}>
                                            <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('subBaseCourse.frictionAngle') || 'Friction Angle'}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColSymbol}>
                                                <Text style={pdfSharedStyles.tableCell}>φ_sb</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColValue}>
                                                <Text style={pdfSharedStyles.tableCell}>{formatValue(geoGridData.subBaseCourse.frictionAngle)}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColUnit}>
                                                <Text style={pdfSharedStyles.tableCell}>°</Text>
                                            </View>
                                        </View>
                                    )}
                                    {geoGridData.subBaseCourse.loadSpreadingAngle !== undefined && (
                                        <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.evenRow]}>
                                            <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('subBaseCourse.loadSpreadingAngle') || 'Load Spreading Angle'}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColSymbol}>
                                                <Text style={pdfSharedStyles.tableCell}>α_sb</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColValue}>
                                                <Text style={pdfSharedStyles.tableCell}>{formatValue(geoGridData.subBaseCourse.loadSpreadingAngle)}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColUnit}>
                                                <Text style={pdfSharedStyles.tableCell}>°</Text>
                                            </View>
                                        </View>
                                    )}
                                    {geoGridData.subBaseCourse.cohesion !== undefined && (
                                        <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.oddRow]}>
                                            <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('subBaseCourse.cohesion') || 'Cohesion'}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColSymbol}>
                                                <Text style={pdfSharedStyles.tableCell}>c_sb</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColValue}>
                                                <Text style={pdfSharedStyles.tableCell}>{formatValue(geoGridData.subBaseCourse.cohesion)}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColUnit}>
                                                <Text style={pdfSharedStyles.tableCell}>{getUnit('kPa', 'psf')}</Text>
                                            </View>
                                        </View>
                                    )}
                                </View>
                            </View>
                        )}

                        {/* Subgrade Table */}
                        {geoGridData?.subgrade && (
                            <View style={pdfSharedStyles.colHalf}>
                                <Text style={pdfSharedStyles.tableTitle}>{t('subgrade')}</Text>
                                <View style={pdfSharedStyles.spacer0} />
                                <View style={pdfSharedStyles.table}>
                                    <View style={pdfSharedStyles.tableRow}>
                                        <View style={[pdfSharedStyles.tableColHeader, pdfSharedStyles.dimFirstCol]}>
                                            <Text style={[pdfSharedStyles.tableCellHeader, pdfSharedStyles.leftCell]}>{t('output_description')}</Text>
                                        </View>
                                        <View style={pdfSharedStyles.tableColHeaderSymbol}>
                                            <Text style={pdfSharedStyles.tableCellHeader}>{t('output_symbol')}</Text>
                                        </View>
                                        <View style={pdfSharedStyles.tableColHeaderValue}>
                                            <Text style={pdfSharedStyles.tableCellHeader}>{t('output_value')}</Text>
                                        </View>
                                        <View style={pdfSharedStyles.tableColHeaderUnit}>
                                            <Text style={pdfSharedStyles.tableCellHeader}>{t('output_unit')}</Text>
                                        </View>
                                    </View>
                                    {/* Show CBR only if subgradeInputMethod is 'cbr' */}
                                    {geoGridData.subgrade.subgradeInputMethod === 'cbr' && geoGridData.subgrade.cbrSubgrade !== undefined && (
                                        <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.evenRow]}>
                                            <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('subgrade.cbrSubgrade')}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColSymbol}>
                                                <Text style={pdfSharedStyles.tableCell}>CBR</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColValue}>
                                                <Text style={pdfSharedStyles.tableCell}>{formatValue(geoGridData.subgrade.cbrSubgrade)}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColUnit}>
                                                <Text style={pdfSharedStyles.tableCell}>%</Text>
                                            </View>
                                        </View>
                                    )}
                                    {/* Show Ev2 only if subgradeInputMethod is 'ev2' or 'modulus' */}
                                    {(geoGridData.subgrade.subgradeInputMethod === 'ev2' || geoGridData.subgrade.subgradeInputMethod === 'modulus') && geoGridData.subgrade.deformationModulus !== undefined && (
                                        <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.oddRow]}>
                                            <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('subgrade.deformationModulus') || 'Deformation Modulus'}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColSymbol}>
                                                <Text style={pdfSharedStyles.tableCell}>E_sg</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColValue}>
                                                <Text style={pdfSharedStyles.tableCell}>{formatValue(geoGridData.subgrade.deformationModulus)}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColUnit}>
                                                <Text style={pdfSharedStyles.tableCell}>{getUnit('MPa', 'psi')}</Text>
                                            </View>
                                        </View>
                                    )}
                                    {geoGridData.subgrade.safetyFactor !== undefined && (
                                        <View style={[pdfSharedStyles.tableRow, pdfSharedStyles.evenRow]}>
                                            <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                                <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t('subgrade.safetyFactor') || 'Safety Factor'}</Text>
                                            </View>
                                            <View style={pdfSharedStyles.tableColSymbol}>
                                                <Text style={pdfSharedStyles.tableCell}>FS</Text>
                                            </View>
                                            <ValueUnitCells value={formatValue(geoGridData.subgrade.safetyFactor)} unit="-" />
                                        </View>
                                    )}
                                </View>
                            </View>
                        )}
                    </View>
                </View>
            )}

            <View break />

            {/* Results Section */}
            <View style={pdfSharedStyles.spacer3} />
            <Text style={[pdfSharedStyles.title, pdfSharedStyles.resultTitle]}>{t('results') || 'Results'}</Text>

            {/* GeoGrid Layer Visualization */}
            {showGeoGridImage && geogridImage && (
                <View style={pdfSharedStyles.section} wrap={false}>
                    <Image style={pdfSharedStyles.graphicImage} src={geogridImage} />
                </View>
            )}

            {/* Base Course Results */}
            {showCourseResults && baseThickness > 0 && baseGeogrids.length > 0 && (
                <View style={pdfSharedStyles.section}>
                    <View style={pdfSharedStyles.spacer1} />
                    <Text style={pdfSharedStyles.tableTitle}>{t('geogrid_base_course_railway', 'Ballast')}</Text>
                    <View style={pdfSharedStyles.spacer0} />
                    <View style={pdfSharedStyles.table}>
                        <View style={pdfSharedStyles.tableRow} fixed>
                            <View style={[pdfSharedStyles.tableColHeader, pdfSharedStyles.dimFirstCol]}>
                                <Text style={[pdfSharedStyles.tableCellHeader, pdfSharedStyles.leftCell]}>{t('output_description')}</Text>
                            </View>
                            <View style={pdfSharedStyles.tableColHeaderSymbol}>
                                <Text style={pdfSharedStyles.tableCellHeader}>{t('output_symbol')}</Text>
                            </View>
                            <View style={pdfSharedStyles.tableColHeaderValue}>
                                <Text style={pdfSharedStyles.tableCellHeader}>{t('output_value')}</Text>
                            </View>
                            <View style={pdfSharedStyles.tableColHeaderUnit}>
                                <Text style={pdfSharedStyles.tableCellHeader}>{t('output_unit')}</Text>
                            </View>
                        </View>
                        {formattedBaseCourseData.map((rowItem, rowIndex) => {
                            if (Array.isArray(rowItem)) {
                                const [label, symbol, value, unit] = rowItem;
                                return (
                                    <View key={rowIndex} style={[pdfSharedStyles.tableRow, rowIndex % 2 === 0 ? pdfSharedStyles.evenRow : pdfSharedStyles.oddRow]} wrap={false}>
                                        <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                            <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{label}</Text>
                                        </View>
                                        <View style={{ ...pdfSharedStyles.tableColSymbol, alignItems: 'center', justifyContent: 'center' }}>
                                            {typeof symbol === 'string' ? (
                                                <Text style={pdfSharedStyles.tableCell}>{symbol}</Text>
                                            ) : (
                                                symbol
                                            )}
                                        </View>
                                        <ValueUnitCells value={value} unit={unit} />
                                    </View>
                                );
                            } else if (rowItem.type === 'productRow') {
                                // Reason: Product row has 2-column layout (Description spans 2 cols, Value spans 2 cols)
                                return (
                                    <View key={rowIndex} style={[pdfSharedStyles.tableRow, rowIndex % 2 === 0 ? pdfSharedStyles.evenRow : pdfSharedStyles.oddRow]} wrap={false}>
                                        <View style={{ width: '50%', borderRightWidth: 1, borderRightColor: '#FFFFFF', padding: 5, justifyContent: 'center' }}>
                                            <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{rowItem.description}</Text>
                                        </View>
                                        <View style={{ width: '50%', padding: 5, justifyContent: 'center' }}>
                                            <Text style={pdfSharedStyles.tableCell}>{rowItem.value}</Text>
                                        </View>
                                    </View>
                                );
                            } else {
                                // Full row for status
                                const isVerified = !rowItem.text.includes('Not Verified');
                                return (
                                    <View key={rowIndex} style={pdfSharedStyles.tableRow} wrap={false}>
                                        <View style={[pdfSharedStyles.tableCol, isVerified ? pdfSharedStyles.verifiedCol : pdfSharedStyles.notVerifiedCol]}>
                                            <Text style={{ ...pdfSharedStyles.verifiedColTableCell, textAlign: 'left', paddingLeft: 10 }}>{rowItem.text}</Text>
                                        </View>
                                    </View>
                                );
                            }
                        })}
                    </View>
                </View>
            )}

            {/* Subbase Course Results */}
            {showCourseResults && subbaseThickness > 0 && subbaseGeogrids.length > 0 && (
                <View style={pdfSharedStyles.section}>
                    <View break />
                    <View style={pdfSharedStyles.spacer1} />
                    <Text style={pdfSharedStyles.tableTitle}>{t('geogrid_subbase_course_railway', 'Subballast')}</Text>
                    <View style={pdfSharedStyles.spacer0} />
                    <View style={pdfSharedStyles.table}>
                        <View style={pdfSharedStyles.tableRow} fixed>
                            <View style={[pdfSharedStyles.tableColHeader, pdfSharedStyles.dimFirstCol]}>
                                <Text style={[pdfSharedStyles.tableCellHeader, pdfSharedStyles.leftCell]}>{t('output_description')}</Text>
                            </View>
                            <View style={pdfSharedStyles.tableColHeaderSymbol}>
                                <Text style={pdfSharedStyles.tableCellHeader}>{t('output_symbol')}</Text>
                            </View>
                            <View style={pdfSharedStyles.tableColHeaderValue}>
                                <Text style={pdfSharedStyles.tableCellHeader}>{t('output_value')}</Text>
                            </View>
                            <View style={pdfSharedStyles.tableColHeaderUnit}>
                                <Text style={pdfSharedStyles.tableCellHeader}>{t('output_unit')}</Text>
                            </View>
                        </View>
                        {formattedSubbaseCourseData.map((rowItem, rowIndex) => {
                            if (Array.isArray(rowItem)) {
                                const [label, symbol, value, unit] = rowItem;
                                return (
                                    <View key={rowIndex} style={[pdfSharedStyles.tableRow, rowIndex % 2 === 0 ? pdfSharedStyles.evenRow : pdfSharedStyles.oddRow]} wrap={false}>
                                        <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                            <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{label}</Text>
                                        </View>
                                        <View style={pdfSharedStyles.tableColSymbol}>
                                            {typeof symbol === 'string' ? (
                                                <Text style={pdfSharedStyles.tableCell}>{symbol}</Text>
                                            ) : (
                                                symbol
                                            )}
                                        </View>
                                        <ValueUnitCells value={value} unit={unit} />
                                    </View>
                                );
                            } else if (rowItem.type === 'productRow') {
                                // Reason: Product row has 2-column layout (Description spans 2 cols, Value spans 2 cols)
                                return (
                                    <View key={rowIndex} style={[pdfSharedStyles.tableRow, rowIndex % 2 === 0 ? pdfSharedStyles.evenRow : pdfSharedStyles.oddRow]} wrap={false}>
                                        <View style={{ width: '50%', borderRightWidth: 1, borderRightColor: '#FFFFFF', padding: 5, justifyContent: 'center' }}>
                                            <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{rowItem.description}</Text>
                                        </View>
                                        <View style={{ width: '50%', padding: 5, justifyContent: 'center' }}>
                                            <Text style={pdfSharedStyles.tableCell}>{rowItem.value}</Text>
                                        </View>
                                    </View>
                                );
                            } else {
                                // Full row for status
                                const isVerified = !rowItem.text.includes('Not Verified');
                                return (
                                    <View key={rowIndex} style={pdfSharedStyles.tableRow} wrap={false}>
                                        <View style={[pdfSharedStyles.tableCol, isVerified ? pdfSharedStyles.verifiedCol : pdfSharedStyles.notVerifiedCol]}>
                                            <Text style={{ ...pdfSharedStyles.verifiedColTableCell, textAlign: 'left', paddingLeft: 10 }}>{rowItem.text}</Text>
                                        </View>
                                    </View>
                                );
                            }
                        })}
                    </View>
                </View>
            )}
        </>
    );
};
